import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Search, Plus, LogOut, Store, MapPin, Users, Warehouse, ScanLine,
  Phone, Mail, Calendar, Clock, StickyNote, Ban, CheckCircle2, Copy, X,
  Smartphone, UploadCloud,
} from "lucide-react";

const STATUS_META = {
  attivo: { label: "Attivo", cls: "bg-emerald-100 text-emerald-700 border-emerald-300" },
  in_scadenza: { label: "In scadenza", cls: "bg-amber-100 text-amber-700 border-amber-300" },
  sospeso: { label: "Sospeso", cls: "bg-red-100 text-red-700 border-red-300" },
};

const MODULES = [
  { key: "casse", label: "Casse", icon: ScanLine },
  { key: "sedi", label: "Sedi", icon: MapPin },
  { key: "operatori", label: "Operatori", icon: Users },
  { key: "magazzino", label: "Magazzino", icon: Warehouse },
];

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("it-IT") : "—");
const fmtDateTime = (iso) => (iso ? new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }) : "mai");

const StatusBadge = ({ status }) => {
  const m = STATUS_META[status] || STATUS_META.attivo;
  return <span data-testid={`status-badge-${status}`} className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold border ${m.cls}`}>{m.label}</span>;
};

export default function ResellerPanel() {
  const { logout } = useAuth();
  const [clients, setClients] = useState(null);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [selected, setSelected] = useState(null); // cliente aperto in dettaglio
  const [showNew, setShowNew] = useState(false);
  const [created, setCreated] = useState(null); // credenziali post-creazione
  const [tab, setTab] = useState("clienti");

  const load = () => api.get("/reseller/clients").then((r) => setClients(Array.isArray(r.data) ? r.data : []));
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!clients) return [];
    const ql = q.toLowerCase();
    return (Array.isArray(clients) ? clients : []).filter((c) => {
      if (statusFilter && c.status !== statusFilter) return false;
      if (!ql) return true;
      return [c.business_name, c.email, c.vat_number, c.phone].filter(Boolean).join(" ").toLowerCase().includes(ql);
    });
  }, [clients, q, statusFilter]);

  const counts = useMemo(() => {
    const base = { attivo: 0, in_scadenza: 0, sospeso: 0 };
    (clients || []).forEach((c) => { base[c.status] = (base[c.status] || 0) + 1; });
    return base;
  }, [clients]);

  const toggleModule = async (client, key, value) => {
    // Aggiornamento ottimistico: un clic, effetto immediato
    const upd = (list) => (Array.isArray(list) ? list : []).map((c) => (c.id === client.id ? { ...c, modules: { ...c.modules, [key]: value } } : c));
    setClients(upd);
    if (selected?.id === client.id) setSelected((s) => ({ ...s, modules: { ...s.modules, [key]: value } }));
    try {
      await api.patch(`/reseller/clients/${client.id}/modules`, { [key]: value });
      toast.success(`${MODULES.find((m) => m.key === key)?.label} ${value ? "attivato" : "disattivato"} — effetto immediato`);
    } catch (e) {
      toast.error(formatApiError(e));
      load();
    }
  };

  return (
    <div className="min-h-screen bg-velta-bg" data-testid="reseller-panel">
      <header className="bg-[#1E2D5B] text-white sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/velta-one-mark-white.png" alt="Velta One" className="w-12 h-7 object-contain" />
            <div className="leading-tight">
              <div className="font-heading font-extrabold tracking-tight">Velta One</div>
              <div className="text-[9px] uppercase tracking-[0.3em] text-white/50">Pannello Rivenditore</div>
            </div>
          </div>
          <button data-testid="reseller-logout-btn" onClick={logout} className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-white">
            <LogOut className="w-4 h-4" /> Esci
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        <div className="flex items-center gap-2 mb-8 border-b border-velta-border">
          {[["clienti", "Clienti", Store], ["aggiornamenti", "Aggiornamenti", Smartphone]].map(([id, label, Icon]) => (
            <button
              key={id}
              data-testid={`reseller-tab-${id}`}
              onClick={() => setTab(id)}
              className={`inline-flex items-center gap-2 px-5 h-12 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === id ? "border-[#1E2D5B] text-[#1E2D5B]" : "border-transparent text-velta-mute hover:text-velta-ink"}`}
            >
              <Icon className="w-4 h-4" /> {label}
            </button>
          ))}
        </div>

        {tab === "aggiornamenti" && <UpdatesSection />}

        {tab === "clienti" && (
        <>
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <div className="text-xs font-mono uppercase tracking-[0.25em] text-velta-mute mb-2">Gestione remota</div>
            <h1 className="font-heading text-4xl font-bold tracking-tight">Clienti</h1>
          </div>
          <button
            data-testid="reseller-new-client-btn"
            onClick={() => setShowNew(true)}
            className="inline-flex items-center gap-2 h-12 px-6 bg-[#1E2D5B] hover:bg-[#162244] text-white rounded-md font-medium btn-tactile"
          >
            <Plus className="w-5 h-5" /> Nuovo cliente
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-3 mb-6">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-velta-mute" />
            <input
              data-testid="reseller-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Cerca per nome, email, P.IVA, telefono..."
              className="h-11 w-full bg-white border border-velta-border rounded-md pl-9 pr-3 outline-none focus:border-[#1E2D5B]"
            />
          </div>
          {[["", `Tutti (${clients?.length ?? 0})`], ["attivo", `Attivi (${counts.attivo})`], ["in_scadenza", `In scadenza (${counts.in_scadenza})`], ["sospeso", `Sospesi (${counts.sospeso})`]].map(([val, label]) => (
            <button
              key={val || "all"}
              data-testid={`reseller-filter-${val || "all"}`}
              onClick={() => setStatusFilter(val)}
              className={`h-11 px-4 rounded-md text-sm font-medium border transition-colors ${statusFilter === val ? "bg-velta-ink text-white border-velta-ink" : "bg-white border-velta-border text-velta-ink hover:border-velta-ink"}`}
            >
              {label}
            </button>
          ))}
        </div>

        {!clients && <div className="text-velta-mute py-16 text-center">Caricamento…</div>}
        {clients && filtered.length === 0 && (
          <div data-testid="reseller-empty" className="bg-white border border-velta-border rounded-md p-12 text-center text-velta-mute">Nessun cliente trovato.</div>
        )}

        <div className="space-y-3">
          {(Array.isArray(filtered) ? filtered : []).map((c) => (
            <div key={c.id} data-testid={`reseller-client-${c.id}`} className="bg-white border border-velta-border rounded-md p-5 hover:border-[#1E2D5B] transition-colors">
              <div className="flex items-start justify-between flex-wrap gap-4">
                <button data-testid={`reseller-client-open-${c.id}`} onClick={() => setSelected(c)} className="text-left group">
                  <div className="flex items-center gap-2.5 mb-1">
                    <Store className="w-4 h-4 text-velta-mute" />
                    <span className="font-heading font-bold text-lg group-hover:text-[#1E2D5B]">{c.business_name}</span>
                    <StatusBadge status={c.status} />
                    {c.is_demo && <span className="text-[10px] font-mono uppercase bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">demo</span>}
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-velta-mute">
                    {c.vat_number && <span>P.IVA {c.vat_number}</span>}
                    {c.email && <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" />{c.email}</span>}
                    {c.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" />{c.phone}</span>}
                    <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" />attivo dal {fmtDate(c.activated_at)}</span>
                    <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />ultimo accesso: {fmtDateTime(c.last_access)}</span>
                  </div>
                </button>
                <div className="flex items-center gap-5">
                  {(Array.isArray(MODULES) ? MODULES : []).map(({ key, label, icon: Icon }) => (
                    <label key={key} className="flex flex-col items-center gap-1.5 cursor-pointer select-none">
                      <span className={`text-[10px] font-mono uppercase tracking-wide inline-flex items-center gap-1 ${c.modules[key] ? "text-velta-ink" : "text-velta-mute"}`}>
                        <Icon className="w-3 h-3" /> {label}
                      </span>
                      <Switch
                        data-testid={`module-switch-${key}-${c.id}`}
                        checked={!!c.modules[key]}
                        onCheckedChange={(v) => toggleModule(c, key, v)}
                      />
                    </label>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
        </>
        )}
      </main>

      {selected && (
        <ClientDetail
          client={selected}
          onClose={() => setSelected(null)}
          onSaved={(upd) => { setClients((l) => (Array.isArray(l) ? l : []).map((c) => (c.id === upd.id ? upd : c))); setSelected(upd); }}
          onToggleModule={toggleModule}
        />
      )}
      {showNew && (
        <NewClientDialog
          onClose={() => setShowNew(false)}
          onCreated={(creds) => { setShowNew(false); setCreated(creds); load(); }}
        />
      )}
      {created && <CredentialsDialog creds={created} onClose={() => setCreated(null)} />}
    </div>
  );
}

function UpdatesSection() {
  const [current, setCurrent] = useState(null);
  const [version, setVersion] = useState("");
  const [apkUrl, setApkUrl] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/reseller/app-update").then((r) => setCurrent(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const publish = async (e) => {
    e.preventDefault();
    if (!version.trim() || !apkUrl.trim()) { toast.error("Inserisci versione e link APK"); return; }
    setBusy(true);
    try {
      const r = await api.post("/reseller/app-update", { version: version.trim(), apk_url: apkUrl.trim() });
      toast.success(`Versione ${r.data.version} pubblicata: disponibile subito per tutti i clienti`);
      setVersion(""); setApkUrl("");
      load();
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="reseller-updates-section" className="max-w-2xl">
      <div className="mb-8">
        <div className="text-xs font-mono uppercase tracking-[0.25em] text-velta-mute mb-2">App Android (APK)</div>
        <h1 className="font-heading text-4xl font-bold tracking-tight">Aggiornamenti</h1>
      </div>

      <div className="bg-white border border-velta-border rounded-md p-5 mb-6 flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-velta-mute mb-1">Versione pubblicata</div>
          {current?.version ? (
            <div className="flex items-center gap-3">
              <span data-testid="update-current-version" className="font-mono font-bold text-2xl">v{current.version}</span>
              <span className="text-xs text-velta-mute">pubblicata il {fmtDateTime(current.published_at)}</span>
            </div>
          ) : (
            <span data-testid="update-none" className="text-velta-mute text-sm">Nessuna versione pubblicata finora.</span>
          )}
        </div>
        {current?.apk_url && (
          <a href={current.apk_url} target="_blank" rel="noreferrer" className="text-xs text-[#1E2D5B] underline break-all max-w-[280px]" data-testid="update-current-link">
            {current.apk_url}
          </a>
        )}
      </div>

      <form onSubmit={publish} className="bg-white border border-velta-border rounded-md p-6 space-y-4">
        <div className="text-sm font-medium">Pubblica una nuova versione</div>
        <Field label="Numero versione" value={version} onChange={setVersion} placeholder="es. 1.0.1" testid="update-version-input" required />
        <Field label="Link diretto al file APK" value={apkUrl} onChange={setApkUrl} placeholder="https://…/velta-one-1.0.1.apk" testid="update-apk-input" required />
        <p className="text-xs text-velta-mute">
          Alla pubblicazione, tutte le app dei clienti mostreranno il badge verde "Aggiornamento disponibile" e proporranno l'installazione al prossimo avvio.
        </p>
        <button data-testid="update-publish-btn" type="submit" disabled={busy} className="w-full h-12 inline-flex items-center justify-center gap-2 bg-[#1E2D5B] hover:bg-[#162244] text-white rounded-md font-medium btn-tactile disabled:opacity-50">
          <UploadCloud className="w-5 h-5" /> {busy ? "Pubblico..." : "Pubblica aggiornamento"}
        </button>
      </form>
    </div>
  );
}

const Field = ({ label, value, onChange, placeholder, testid, type = "text", required }) => (
  <div>
    <label className="block text-[10px] font-mono uppercase tracking-widest text-velta-mute mb-1.5">{label}{required && " *"}</label>
    <input
      data-testid={testid}
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="h-11 w-full bg-white border border-velta-border rounded-md px-3 outline-none focus:border-[#1E2D5B] text-sm"
    />
  </div>
);

function ClientDetail({ client, onClose, onSaved, onToggleModule }) {
  const [form, setForm] = useState({
    business_name: client.business_name || "",
    vat_number: client.vat_number || "",
    address: client.address || "",
    phone: client.phone || "",
    notes: client.notes || "",
    plan: client.plan || "basic",
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/reseller/clients/${client.id}`, form);
      toast.success("Cliente aggiornato");
      onSaved({ ...client, ...form, status: client.suspended ? "sospeso" : client.status });
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const toggleSuspend = async () => {
    const next = !client.suspended;
    setBusy(true);
    try {
      await api.patch(`/reseller/clients/${client.id}`, { suspended: next });
      toast.success(next ? "Cliente sospeso: accesso bloccato" : "Cliente riattivato");
      onSaved({ ...client, ...form, suspended: next, status: next ? "sospeso" : "attivo" });
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="reseller-client-detail" aria-describedby={undefined} className="max-w-2xl max-h-[90vh] overflow-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl flex items-center gap-3">
            {client.business_name} <StatusBadge status={client.suspended ? "sospeso" : client.status} />
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3 text-xs text-velta-mute mb-2">
          <span className="inline-flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> Attivazione: <strong className="text-velta-ink">{fmtDate(client.activated_at)}</strong></span>
          <span className="inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> Ultimo accesso: <strong className="text-velta-ink">{fmtDateTime(client.last_access)}</strong></span>
          {client.email && <span className="inline-flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" /> {client.email}</span>}
          {client.next_due_date && <span className="inline-flex items-center gap-1.5">Scadenza: <strong className="text-velta-ink">{fmtDate(client.next_due_date)}</strong></span>}
        </div>

        <div className="border border-velta-border rounded-md p-4 bg-velta-bg">
          <div className="text-[10px] font-mono uppercase tracking-widest text-velta-mute mb-3">Moduli attivi — effetto immediato</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {(Array.isArray(MODULES) ? MODULES : []).map(({ key, label, icon: Icon }) => (
              <label key={key} className="flex flex-col items-center gap-2 bg-white border border-velta-border rounded-md p-3 cursor-pointer">
                <Icon className={`w-5 h-5 ${client.modules[key] ? "text-[#1E2D5B]" : "text-velta-subtle"}`} />
                <span className="text-xs font-medium">{label}</span>
                <Switch data-testid={`detail-module-switch-${key}`} checked={!!client.modules[key]} onCheckedChange={(v) => onToggleModule(client, key, v)} />
              </label>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Ragione sociale" value={form.business_name} onChange={set("business_name")} testid="detail-business-name" />
          <Field label="P.IVA" value={form.vat_number} onChange={set("vat_number")} testid="detail-vat" />
          <Field label="Indirizzo" value={form.address} onChange={set("address")} testid="detail-address" />
          <Field label="Telefono" value={form.phone} onChange={set("phone")} testid="detail-phone" />
        </div>

        <div>
          <label className="block text-[10px] font-mono uppercase tracking-widest text-velta-mute mb-1.5 flex items-center gap-1"><StickyNote className="w-3 h-3" /> Note interne (visibili solo a te)</label>
          <textarea
            data-testid="detail-notes"
            value={form.notes}
            onChange={(e) => set("notes")(e.target.value)}
            rows={3}
            placeholder="es. pagamento annuale, contattare a dicembre..."
            className="w-full bg-amber-50 border border-amber-200 rounded-md px-3 py-2 outline-none focus:border-amber-400 text-sm"
          />
        </div>

        <div className="flex items-center justify-between gap-3 pt-1">
          <button
            data-testid="detail-suspend-btn"
            onClick={toggleSuspend}
            disabled={busy}
            className={`inline-flex items-center gap-2 h-11 px-4 rounded-md text-sm font-medium border ${client.suspended ? "border-emerald-300 text-emerald-700 hover:bg-emerald-50" : "border-red-300 text-red-600 hover:bg-red-50"}`}
          >
            {client.suspended ? <><CheckCircle2 className="w-4 h-4" /> Riattiva cliente</> : <><Ban className="w-4 h-4" /> Sospendi cliente</>}
          </button>
          <button data-testid="detail-save-btn" onClick={save} disabled={busy} className="h-11 px-6 bg-[#1E2D5B] hover:bg-[#162244] text-white rounded-md text-sm font-medium btn-tactile disabled:opacity-50">
            {busy ? "Salvo..." : "Salva modifiche"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function NewClientDialog({ onClose, onCreated }) {
  const [form, setForm] = useState({
    business_name: "", email: "", vat_number: "", phone: "", address: "",
    plan: "basic", mode: "negozio", modules: { casse: true, sedi: true, operatori: true, magazzino: true },
  });
  const [modes, setModes] = useState([]);
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    api.get("/modes?include_hidden=true").then((r) => setModes(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.business_name.trim() || !form.email.trim()) { toast.error("Ragione sociale ed email sono obbligatorie"); return; }
    setBusy(true);
    try {
      const r = await api.post("/reseller/clients", {
        business_name: form.business_name,
        email: form.email,
        vat_number: form.vat_number || null,
        phone: form.phone || null,
        address: form.address || null,
        plan: form.plan,
        modes: [form.mode],
        modules: form.modules,
      });
      toast.success("Cliente creato e attivo");
      onCreated(r.data);
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="reseller-new-dialog" aria-describedby={undefined} className="max-w-xl max-h-[90vh] overflow-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Nuovo cliente</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Ragione sociale" value={form.business_name} onChange={set("business_name")} placeholder="Bar Roma Srl" testid="new-business-name" required />
            <Field label="Email titolare" value={form.email} onChange={set("email")} placeholder="titolare@barroma.it" type="email" testid="new-email" required />
            <Field label="P.IVA" value={form.vat_number} onChange={set("vat_number")} placeholder="IT01234567890" testid="new-vat" />
            <Field label="Telefono" value={form.phone} onChange={set("phone")} placeholder="333 1234567" testid="new-phone" />
            <div className="col-span-2">
              <Field label="Indirizzo" value={form.address} onChange={set("address")} placeholder="Via Roma 1, Milano" testid="new-address" />
            </div>
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-widest text-velta-mute mb-1.5">Tipo attività</label>
              <select data-testid="new-mode" value={form.mode} onChange={(e) => set("mode")(e.target.value)} className="h-11 w-full bg-white border border-velta-border rounded-md px-3 outline-none focus:border-[#1E2D5B] text-sm">
                {(modes.length ? modes : [{ id: "negozio", label: "Negozio (generico)" }]).map((m) => (
                  <option key={m.id} value={m.id}>{m.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="border border-velta-border rounded-md p-4 bg-velta-bg">
            <div className="text-[10px] font-mono uppercase tracking-widest text-velta-mute mb-3">Moduli acquistati</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {(Array.isArray(MODULES) ? MODULES : []).map(({ key, label, icon: Icon }) => (
                <label key={key} className="flex flex-col items-center gap-2 bg-white border border-velta-border rounded-md p-3 cursor-pointer">
                  <Icon className={`w-5 h-5 ${form.modules[key] ? "text-[#1E2D5B]" : "text-velta-subtle"}`} />
                  <span className="text-xs font-medium">{label}</span>
                  <Switch data-testid={`new-module-${key}`} checked={form.modules[key]} onCheckedChange={(v) => setForm((f) => ({ ...f, modules: { ...f.modules, [key]: v } }))} />
                </label>
              ))}
            </div>
          </div>

          <p className="text-xs text-velta-mute">La password del titolare viene generata automaticamente e mostrata dopo la creazione. Il cliente è attivo da subito.</p>

          <button data-testid="new-submit-btn" type="submit" disabled={busy} className="w-full h-12 bg-[#1E2D5B] hover:bg-[#162244] text-white rounded-md font-medium btn-tactile disabled:opacity-50">
            {busy ? "Creo..." : "Crea cliente e attiva"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function CredentialsDialog({ creds, onClose }) {
  const copy = () => {
    navigator.clipboard?.writeText(`Velta One — Accesso\nEmail: ${creds.email}\nPassword: ${creds.password}\nLogin: ${window.location.origin}/login`);
    toast.success("Credenziali copiate");
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="reseller-creds-dialog" aria-describedby={undefined} className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl flex items-center gap-2"><CheckCircle2 className="w-6 h-6 text-emerald-600" /> Cliente attivo</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-velta-mute">Consegna queste credenziali al cliente. Potrà accedere subito da <strong>{window.location.origin}/login</strong>.</p>
        <div className="bg-velta-bg border-2 border-velta-border rounded-md p-4 space-y-2 font-mono text-sm">
          <div><span className="text-velta-mute">Email:</span> <strong data-testid="creds-email">{creds.email}</strong></div>
          <div><span className="text-velta-mute">Password:</span> <strong data-testid="creds-password">{creds.password}</strong></div>
        </div>
        <div className="flex gap-3">
          <button data-testid="creds-copy-btn" onClick={copy} className="flex-1 h-11 inline-flex items-center justify-center gap-2 bg-[#1E2D5B] hover:bg-[#162244] text-white rounded-md text-sm font-medium btn-tactile">
            <Copy className="w-4 h-4" /> Copia credenziali
          </button>
          <button data-testid="creds-close-btn" onClick={onClose} className="h-11 px-5 inline-flex items-center gap-2 border border-velta-border rounded-md text-sm font-medium hover:bg-velta-bg">
            <X className="w-4 h-4" /> Chiudi
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
